import React, { useEffect, useState, useCallback } from "react";
import * as Styled from "./ManageBlocksComponent.styled";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { blocksService, userService } from "../../services";
import { Loader } from "../ui";
import { IBlock, IBlockCreateRequest, IBlockUpdateRequest, LayoutImages, LayoutTemplates, BlockType, ConfirmModal, FullScreenModal, LibraryType } from "../../libs";
import { toast } from "react-toastify";
import { ArrowLeft, Eye, Pencil, Plus, Save, Trash, XLg } from "react-bootstrap-icons";
import { confirmAlert } from "react-confirm-alert";
import SearchIcon from "../../assets/images/icons/search.svg";
import { ThemeProvider } from "@emotion/react";
import { createTheme } from "@mui/material";
import { ReactComponent as ReorderIcon } from "../../assets/images/icons/reorder-icon.svg";
import { ReactComponent as InfoIcon } from "../../assets/images/icons/info-icon .svg";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import DefaultThumbnailImage from "../../assets/images/default_thumbnail.jpeg";
import { Modal } from "react-bootstrap";
import { NewBlockForm } from "./new-block-form";
import { EditBlockForm } from "./edit-block-form";
import { useAuth } from "../../behavioral";



export const ManageBlocksComponent: React.FC = () => {
    const navigate = useNavigate();
    const { profileId, versionId } = useParams<{ profileId: string; versionId: string }>();
    const { state } = useLocation();
    const [version, setVersion] = useState(state?.version || null);
    const [blocks, setBlocks] = useState<IBlock[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [showNewBlockForm, setShowNewBlockForm] = useState(false);
    const [showEditBlockForm, setShowEditBlockForm] = useState(false);
    const [editingBlock, setEditingBlock] = useState<IBlock | null>(null);
    const [isDragMode, setIsDragMode] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalBlocks, setOriginalBlocks] = useState<IBlock[]>([]);
    const [isFullscreenPreview, setIsFullscreenPreview] = useState(false);
    const [previewRenderKey, setPreviewRenderKey] = useState(0);
    const [darkMode, setDarkMode] = useState(false);
    const authInfo = useAuth();


    const fetchBlocks = useCallback(async (disableLoading?: boolean) => {
        if (!profileId || !versionId) return;

        if (!disableLoading) setLoading(true);
        try {
            const data = await blocksService.getBlocks(profileId, versionId);
            setBlocks(data);
            setOriginalBlocks(data);
            setHasUnsavedChanges(false);
        } catch (error) {
            console.error("Error fetching blocks:", error);
            toast.error("Failed to load blocks");
        } finally {
            setLoading(false);
        }
    }, [profileId, versionId]);

    const fetchVersion = useCallback(async () => {
        if (!profileId || version) return;

        try {
            const profiles = await blocksService.getProfiles();
            const profile = profiles.find(p => p.profileId === profileId);
            if (profile) {
                const versions = await blocksService.getVersions(profileId);
                const currentVersion = versions.find(v => v.versionId === versionId);
                if (currentVersion) {
                    setVersion(currentVersion);
                }
            }
        } catch (error) {
            console.error("Error fetching version:", error);
            toast.error("Failed to load version");
        }
    }, [profileId, versionId, version]);

    useEffect(() => {
        fetchVersion();
        fetchBlocks();
    }, [fetchVersion, fetchBlocks]);

    useEffect(() => {
        userService.getLibraryConfig(LibraryType.MediaLibrary).then((result) => {
            setDarkMode(result?.darkMode || false);
        });
    }, []);

    const handleDeleteBlock = useCallback(async (blockId: string, block: IBlock) => {
        if (!profileId || !versionId) return;

        const blockTypeName = block.type === BlockType.GENERAL_VOD ? "General VOD" :
            block.type === BlockType.FILM_FESTIVAL_VOD ? "Film Festival VOD" :
                block.type === BlockType.LIVE_STREAMS ? "Live Streams" :
                    block.type === BlockType.PLAYLIST ? block.payload?.title || "Playlist" :
                        block.type === BlockType.AD ? "Ad" : "Unknown Type";

        confirmAlert({
            customUI: ({ onClose }) => (
                <ConfirmModal
                    title="Delete Block"
                    description={[{ text: `Are you sure you want to delete the block "${blockTypeName}"? This action cannot be undone.`, bold: false }]}
                    noText="Cancel"
                    yesText="Delete"
                    danger={true}
                    onNo={() => {
                        onClose();
                    }}
                    onYes={async () => {
                        try {
                            await blocksService.deleteBlock(profileId, versionId, blockId);
                            toast.success("Block deleted successfully");
                            await fetchBlocks(true);
                            setPreviewRenderKey(prev => prev + 1);
                        } catch (error) {
                            console.error("Error deleting block:", error);
                            toast.error("Failed to delete block");
                        } finally {
                            onClose();
                        }
                    }}
                />
            ),
        });
    }, [profileId, versionId, fetchBlocks]);

    const handleEditBlock = useCallback((block: IBlock) => {
        setEditingBlock(block);
        setShowEditBlockForm(true);
    }, []);

    const updateBlock = useCallback(async (blockId: string, data: IBlockUpdateRequest) => {
        if (!profileId || !versionId) return;

        try {
            await blocksService.updateBlock(profileId, versionId, blockId, data);
            toast.success("Block updated successfully");
            await fetchBlocks(true);
            setShowEditBlockForm(false);
            setEditingBlock(null);
            setPreviewRenderKey(prev => prev + 1);
        } catch (error) {
            console.error("Error updating block:", error);
            toast.error("Failed to update block");
        }
    }, [profileId, versionId, fetchBlocks]);

    const filteredData = React.useMemo(() => {
        // TODO: Implement search functionality when block properties are defined
        return blocks.filter(item =>
            true
        );
    }, [blocks]);

    const theme = createTheme({
        palette: {
            mode: "dark",
        },
    });

    const handleIndexChange = useCallback((blockIndex: number, newIndex: string) => {
        let targetIndex = parseInt(newIndex) - 1;

        if (isNaN(targetIndex)) {
            return;
        }

        setBlocks(prevBlocks => {
            targetIndex = Math.max(0, Math.min(targetIndex, prevBlocks.length - 1));

            const newBlocks = [...prevBlocks];
            const movedBlock = newBlocks[blockIndex];
            newBlocks.splice(blockIndex, 1);
            newBlocks.splice(targetIndex, 0, movedBlock);

            return newBlocks;
        });
        setHasUnsavedChanges(true);
    }, []);

    const columns = React.useMemo<MRT_ColumnDef<IBlock>[]>(() => [
        {
            header: "#",
            headerAlign: 'center',

            Cell: ({ row }) => {
                if (isDragMode) {
                    return (
                        <Styled.NumberHolderWrapper>
                            <Styled.NumberInput
                                type="number"
                                min="1"
                                max={blocks.length}
                                key={`input-${row.original.blockId}-${row.index}`}
                                defaultValue={row.index + 1}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleIndexChange(row.index, e.currentTarget.value);
                                        e.currentTarget.blur();
                                    }
                                }}
                            />
                        </Styled.NumberHolderWrapper>
                    );
                }
                return (
                    <Styled.NumberHolderWrapper>
                        <Styled.NumberHolder>
                            {row.index + 1}
                        </Styled.NumberHolder>
                    </Styled.NumberHolderWrapper>
                );
            },
            enableSorting: false,
            size: 40,
        },
        {
            header: "Template",
            accessorKey: "template",
            size: 87,
            Cell: ({ row }) => (
                <Styled.ThumbnailImage
                    src={LayoutImages[row.original.renderInfo.templateId as LayoutTemplates] || DefaultThumbnailImage}
                    alt="Thumbnail"
                />
            ),
        },
        {
            header: "Block",
            accessorKey: "block",
            size: 200,
            Cell: ({ row }) => <span style={{ fontSize: "14px", fontWeight: 700 }}>{
                row.original.type === BlockType.GENERAL_VOD ? "General VOD" :
                    row.original.type === BlockType.FILM_FESTIVAL_VOD ? "Film Festival VOD" :
                        row.original.type === BlockType.LIVE_STREAMS ? "Live Streams" :
                            row.original.type === BlockType.PLAYLIST ? row.original.payload?.title || "Playlist" :
                                row.original.type === BlockType.AD ? "Ad" :
                                    "Unknown Type"
            }</span>,
        },
        {
            header: "Created Date",
            accessorKey: "publishedDateFormatted",
            size: 120,
            Cell: ({ row }) => (
                <span style={{ fontSize: "14px", fontWeight: 400, color: "#9F9F9F99" }}>
                    {new Date(row.original.createdAt).toLocaleDateString()}
                </span>
            ),
        },
        {
            header: "Actions",
            accessorKey: "actions",
            size: 100,
            Cell: ({ row }) => (
                <Styled.ActionsCell>
                    <Styled.CustomActionButton
                        variant="outline-secondary"
                        onClick={() => handleEditBlock(row.original)}
                        title="Edit Block"
                    >
                        <Pencil />
                    </Styled.CustomActionButton>
                    <Styled.CustomActionButton
                        variant="outline-danger"
                        onClick={() => handleDeleteBlock(row.original.blockId, row.original)}
                        title="Delete Block"
                    >
                        <Trash />
                    </Styled.CustomActionButton>
                </Styled.ActionsCell>
            ),
            enableSorting: false,
        }
    ], [isDragMode, blocks.length, handleIndexChange, handleEditBlock, handleDeleteBlock]);

    const table = useMaterialReactTable({
        autoResetPageIndex: false,
        columns,
        data: filteredData,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableFilters: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enablePagination: false,
        manualPagination: false,
        paginationDisplayMode: 'pages',
        renderTopToolbar: '',
        enableRowDragging: isDragMode,
        enableRowOrdering: isDragMode,
        muiRowDragHandleProps: ({ row }) => ({
            onDragEnd: () => {
                const { draggingRow, hoveredRow } = table.getState();
                if (hoveredRow && draggingRow) {
                    handleReorderBlocks(
                        draggingRow.index,
                        hoveredRow.index || 0
                    );
                }
            },
        }),
        initialState: {
            showColumnFilters: false,
            showGlobalFilter: false,
            showToolbarDropZone: false,
            density: 'comfortable',
            pagination: {
                pageIndex: 0,
                pageSize: 10,
            },
        },

        muiPaginationProps: {
            showRowsPerPage: true,
            showFirstButton: true,
            showLastButton: true,
        },
        muiTableHeadRowProps: {
            sx: {
                borderBottom: '2px solid #1d1f24',
            },
        },

        positionPagination: "bottom",
        muiTableBodyCellProps: {
            sx: {
                borderBottom: 'none !important',
            },
        },
        mrtTheme: {
            baseBackgroundColor: "#0F1013",
            draggingBorderColor: "#b6f84d",
        },
    });

    const createNewBlock = useCallback(async (data: IBlockCreateRequest) => {
        if (!profileId || !versionId) return;

        try {
            await blocksService.createBlock(profileId, versionId, data);
            toast.success("Block created successfully");
            await fetchBlocks(true);
            setShowNewBlockForm(false);
            setPreviewRenderKey(prev => prev + 1);
        } catch (error) {
            console.error("Error creating block:", error);
            toast.error("Failed to create block");
        }
    }, [profileId, versionId, fetchBlocks]);

    const handleReorderBlocks = useCallback((draggedRowIndex: number, targetRowIndex: number) => {
        setBlocks(prevBlocks => {
            const newBlocks = [...prevBlocks];
            const draggedBlock = newBlocks[draggedRowIndex];
            newBlocks.splice(draggedRowIndex, 1);
            newBlocks.splice(targetRowIndex, 0, draggedBlock);
            return newBlocks;
        });
        setHasUnsavedChanges(true);
    }, []);

    const handleSaveOrder = useCallback(async () => {
        if (!profileId || !versionId || !hasUnsavedChanges) {
            toast.info("No changes to save");
            return;
        };

        try {
            await blocksService.updateBlocksOrder(profileId, versionId, blocks.map(block => block.blockId));
            toast.success("Block order saved successfully");
            setOriginalBlocks(blocks);
            setHasUnsavedChanges(false);
            setIsDragMode(false);
            setPreviewRenderKey(prev => prev + 1);
        } catch (error) {
            console.error("Error saving block order:", error);
            toast.error("Failed to save block order");
        }
    }, [profileId, versionId, blocks, hasUnsavedChanges]);

    const handleCancelReorder = useCallback(() => {
        setBlocks(originalBlocks);
        setHasUnsavedChanges(false);
        setIsDragMode(false);
    }, [originalBlocks]);


    const srcDocContent = React.useMemo(() => {
        const CDN_URL = process.env.REACT_APP_CDN_URL;
        const EMBED_MEDIA_LIBRARY_HTML = `
            <!doctype html>
            <html>
            <head>
            <link rel="stylesheet" href="${CDN_URL}/membership/index.css" />
            <link rel="stylesheet" href="${CDN_URL}/media/index.css" />
            <style>body { background-color: ${darkMode ? '#121212' : '#ffffff'}; }</style>
            </head>
            <body>
            <div style="padding: 20px;">
            <div id="cembooClientJoinNowButton"></div>
            <div id="cemboo-media-library"></div>

            </div>
            <script src="${CDN_URL}/membership/index.js"></script>
            <script src="${CDN_URL}/media/index.js"></script>

            <script>
                const clientId = "${authInfo?.user?.username}";
                const outletId = "preview-mode-outlet";
                const profileId = "${profileId}";
                const versionId = "${versionId}";

                const createCembooMediaLibrary = ({ previewMode, profileId, versionId }) =>
                    new window.CembooMediaLibrary(
                        clientId,
                        "false",
                        false,
                        false,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        outletId,
                        previewMode,
                        profileId,
                        versionId
                    );

                window.onload = () => {
                initiateCembooMemberSpace();
                const CembooMediaLibrary = createCembooMediaLibrary({
                    previewMode: true,
                    profileId,
                    versionId
                });
                const cembooMediaLibrary = CembooMediaLibrary.init();
                };
                const initiateCembooMemberSpace = () => {
                new window.CembooMemberSpace(clientId).init();
                };
            </script>
            </body>
            </html>
        `;
        console.log("Generated srcDocContent for iframes:", EMBED_MEDIA_LIBRARY_HTML);
        return EMBED_MEDIA_LIBRARY_HTML;
    }, [profileId, versionId, authInfo?.user?.username, darkMode]);

    return loading ? (
        <Loader />
    ) : (
        <>
            <Styled.PageLayout>
                <Styled.LeftSection>
                    <ThemeProvider theme={theme}>
                        <Styled.StyledContainer>
                            <Styled.Header>
                                <Styled.HeaderLeftSection>
                                    <Styled.TitleRow>
                                        <Styled.VersionName>
                                            {state ? `${state.profile.profileName}: ${state.version.versionName}` : "Version"}
                                        </Styled.VersionName>
                                    </Styled.TitleRow>
                                </Styled.HeaderLeftSection>
                                <Styled.HeaderRightSection>
                                    {!isDragMode && (
                                        <>
                                            <Styled.CustomButton
                                                variant="outline-secondary"
                                                hoverWidth="200px"
                                                onClick={() => setIsFullscreenPreview(true)}
                                            >
                                                <Styled.IconWrapper>
                                                    <Eye />
                                                </Styled.IconWrapper>
                                                <Styled.Label>Fullscreen Preview</Styled.Label>
                                            </Styled.CustomButton>

                                            <Styled.CustomButton
                                                variant="outline-secondary"
                                                onClick={() => setIsDragMode(true)}
                                            >
                                                <Styled.IconWrapper>
                                                    <ReorderIcon />
                                                </Styled.IconWrapper>
                                                <Styled.Label>Reorder</Styled.Label>
                                            </Styled.CustomButton>
                                            <Styled.CustomButton variant="primary" onClick={() => setShowNewBlockForm(true)}>
                                                <Styled.IconWrapper>
                                                    <Plus />
                                                </Styled.IconWrapper>
                                                <Styled.Label>New Block</Styled.Label>
                                            </Styled.CustomButton>
                                        </>
                                    )}
                                    {isDragMode && (
                                        <>
                                            <Styled.CustomButton
                                                variant="primary"
                                                onClick={handleSaveOrder}
                                                disabled={!hasUnsavedChanges}
                                            >
                                                <Styled.IconWrapper>
                                                    <Save />
                                                </Styled.IconWrapper>
                                                <Styled.Label>Save Order</Styled.Label>
                                            </Styled.CustomButton>
                                            <Styled.CustomButton
                                                variant="outline-secondary"
                                                onClick={handleCancelReorder}
                                            >
                                                <Styled.IconWrapper>
                                                    <XLg />
                                                </Styled.IconWrapper>
                                                <Styled.Label>Cancel</Styled.Label>
                                            </Styled.CustomButton>
                                        </>
                                    )}
                                    <Styled.CustomButton variant="outline-secondary" onClick={() => { navigate(-1); }}>
                                        <Styled.IconWrapper>
                                            <ArrowLeft />
                                        </Styled.IconWrapper>
                                        <Styled.Label>Go Back</Styled.Label>
                                    </Styled.CustomButton>
                                </Styled.HeaderRightSection>
                            </Styled.Header>

                            <Styled.Description>
                                <Styled.DescriptionLeftSection>
                                    <Styled.InfoIconStyled>
                                        <InfoIcon />
                                    </Styled.InfoIconStyled>
                                    {isDragMode
                                        ? "Drag and drop rows to reorder blocks. Click 'Save Order' to save changes or 'Cancel' to discard."
                                        : "There are some videos that are repeated in other content folders."
                                    }
                                </Styled.DescriptionLeftSection>
                                <Styled.DescriptionRightSection>
                                    <Styled.DashboardSearchWrapper>
                                        <Styled.SearchBarWrapper>
                                            <Styled.SearchIcon src={SearchIcon} alt="Search" />
                                            <Styled.SearchInput
                                                name="search"
                                                type="text"
                                                placeholder="Search by title"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                        </Styled.SearchBarWrapper>
                                    </Styled.DashboardSearchWrapper>
                                </Styled.DescriptionRightSection>
                            </Styled.Description>

                            <Styled.TableWrapper>
                                {filteredData.length === 0 ? (
                                    <Styled.NoResultsText>No results found.</Styled.NoResultsText>
                                ) : (
                                    <MaterialReactTable table={table} />

                                )}

                            </Styled.TableWrapper>
                        </Styled.StyledContainer>
                    </ThemeProvider>
                </Styled.LeftSection>

                <Styled.RightSection key={previewRenderKey}>
                    <Styled.PreviewWrapper>
                        <Styled.PreviewIframeWrapper>
                            <Styled.LandscapeIframe
                                title="Landscape Preview"
                                // src={`http://localhost:3001?profileId=${profileId}&versionId=${versionId}`}
                                srcDoc={srcDocContent}
                            />
                        </Styled.PreviewIframeWrapper>
                        <Styled.PortraitIframeWrapper>
                            <Styled.PortraitIframe
                                title="Portrait Preview"
                                // src={`http://localhost:3001?profileId=${profileId}&versionId=${versionId}`}
                                srcDoc={srcDocContent}
                            />
                        </Styled.PortraitIframeWrapper>
                    </Styled.PreviewWrapper>
                </Styled.RightSection>
            </Styled.PageLayout>
            <NewBlockForm
                show={showNewBlockForm}
                onClose={() => setShowNewBlockForm(false)}
                onCreateBlock={createNewBlock}
            />
            <EditBlockForm
                show={showEditBlockForm}
                block={editingBlock}
                onClose={() => {
                    setShowEditBlockForm(false);
                    setEditingBlock(null);
                }}
                onUpdateBlock={(blockId, data) => updateBlock(blockId, data)}
            />

            <FullScreenModal
                show={isFullscreenPreview}
                onHide={() => setIsFullscreenPreview(false)}
                header={
                    <Styled.FullscreenModalHeader closeButton>
                        <Modal.Title>
                            {state ? `${state.profile.profileName}: ${state.version.versionName}` : "Version"}
                        </Modal.Title>
                    </Styled.FullscreenModalHeader>
                }
                body={
                    <Styled.LargePreviewIframeWrapper>
                        <Styled.LargeLandscapeIframe
                            title="Landscape Fullscreen Preview"
                            srcDoc={srcDocContent}
                        />
                    </Styled.LargePreviewIframeWrapper>
                }
            />


        </>
    );
};
